import { useMemo } from "react"
import styled from "styled-components"
import { FaCopy, FaEdit, FaHistory, FaPlay, FaPowerOff, FaTrash } from "react-icons/fa"
import Table from "../../sharedComponents/Table"
import useMe from "../../hooks/useMe"
import { formatDateTime, formatSchedule } from "./jobUtils"

const TableWrapper = styled.div`
  height: calc(100vh - 250px);
  min-height: 420px;
`

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 74px;
  height: 24px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: bold;
  color: ${({ $status }) => $status === 'failure' ? '#8a1111' : ($status === 'success' ? '#0f5b31' : '#565656')};
  background: ${({ $status }) => $status === 'failure' ? '#fde6e6' : ($status === 'success' ? '#e6f5ed' : '#f1f1f1')};
`

const MutedText = styled.div`
  color: ${({ theme }) => theme.colors.lightText};
  font-size: 13px;
`

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: ${({ theme }) => theme.borders.main};
  border-radius: ${({ theme }) => theme.borderRadius.main};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.backgrounds.pure};
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 100%;
  padding: 5px 0;
`

export { StatusBadge, MutedText, IconButton }

export default ({
  jobs,
  search,
  isLoading,
  onRun,
  onHistory,
  onEdit,
  onClone,
  onToggle,
  onDelete
}) => {
  const { hasEdit } = useMe()

  const cols = useMemo(() => ([
    {
      headerName: 'Job',
      field: 'name',
      tooltipField: 'name',
      minWidth: 250
    },
    {
      headerName: 'State',
      field: 'enabled',
      maxWidth: 110,
      cellRenderer: ({ data }) => (
        <StatusBadge $status={data.enabled ? 'success' : 'disabled'}>
          {data.enabled ? 'Enabled' : 'Disabled'}
        </StatusBadge>
      )
    },
    {
      headerName: 'Schedule',
      valueGetter: ({ data }) => formatSchedule(data),
      tooltipValueGetter: ({ data }) => formatSchedule(data),
      minWidth: 190
    },
    {
      headerName: 'Endpoint',
      field: 'endpointUrl',
      tooltipField: 'endpointUrl',
      minWidth: 220
    },
    {
      headerName: 'Next Run',
      field: 'nextRunAt',
      valueFormatter: ({ value }) => formatDateTime(value),
      tooltipValueGetter: ({ value }) => formatDateTime(value),
      minWidth: 150
    },
    {
      headerName: 'Last Result',
      field: 'lastStatus',
      maxWidth: 125,
      cellRenderer: ({ data }) => data.lastStatus
        ? <StatusBadge $status={data.lastStatus}>{data.lastStatus}</StatusBadge>
        : <MutedText>Not run</MutedText>
    },
    {
      headerName: 'Actions',
      minWidth: 235,
      cellRenderer: ({ data }) => (
        <Actions>
          {hasEdit && 
            <IconButton title='Run now' onClick={() => onRun(data)} disabled={data.isRunning}>
              <FaPlay />
            </IconButton>
          }
          <IconButton title='History' onClick={() => onHistory(data)}>
            <FaHistory />
          </IconButton>
          {hasEdit && 
          <>
            <IconButton title='Edit' onClick={() => onEdit(data)}>
              <FaEdit />
            </IconButton>
            <IconButton title='Clone' onClick={() => onClone(data)}>
              <FaCopy />
            </IconButton>
            <IconButton title={data.enabled ? 'Disable' : 'Enable'} onClick={() => onToggle(data)}>
              <FaPowerOff />
            </IconButton>
            <IconButton title='Delete' onClick={() => onDelete(data)}>
              <FaTrash />
            </IconButton>
          </>
          }
        </Actions>
      )
    }
  ]), [onRun, onHistory, onEdit, onClone, onToggle, onDelete, hasEdit])

  return (
    <TableWrapper>
      <Table
        data={jobs}
        cols={cols}
        filter={search}
        isDisableAutoHeight={true}
        isLoading={isLoading}
      />
    </TableWrapper>
  )
}
